import classNames from 'classnames/bind';
import { motion } from 'framer-motion';
import { graphql, useStaticQuery } from 'gatsby';
import GatsbyImage from 'gatsby-image';
import React, { useState } from 'react';

import { APP_NAMES, APPS_CONTENT } from 'components/pages/home/offer/data';
import Heading from 'components/shared/heading';
import { BREAKPOINT_LG } from 'constants/breakpoints';
import useDeviceType, { DEVICE_TYPE } from 'hooks/use-device-type';
import useWindowWidth from 'hooks/use-window-width';

import OfferVertical from './offer-tablet';
import styles from './offer.module.scss';
import Slide from './slide';

const cx = classNames.bind(styles);
const TRANSITION_DURATION = 0.8;

const arrowVariants = {
  phantom: {
    width: '34rem',
    top: '3rem',
    transition: {
      duration: TRANSITION_DURATION,
    },
  },
  dawn: {
    width: '21rem',
    top: '16.875rem',
    transition: {
      duration: TRANSITION_DURATION,
    },
  },
  wraith: {
    width: '27rem',
    top: '9.875rem',
    transition: {
      duration: TRANSITION_DURATION,
    },
  },
  cullinan: {
    width: '33rem',
    top: '23.875rem',
    transition: {
      duration: TRANSITION_DURATION,
    },
  },
};

const arrowVariantsSmallScreen = {
  phantom: {
    width: '34rem',
    top: '3rem',
    transition: {
      duration: TRANSITION_DURATION,
    },
  },
  dawn: {
    width: '21rem',
    top: '17.875rem',
    transition: {
      duration: TRANSITION_DURATION,
    },
  },
  wraith: {
    width: '27rem',
    top: '10.5rem',
    transition: {
      duration: TRANSITION_DURATION,
    },
  },
  cullinan: {
    width: '33rem',
    top: '25.125rem',
    transition: {
      duration: TRANSITION_DURATION,
    },
  },
};

const slidesVariants = {
  phantom: {
    y: 0,
    transition: {
      duration: TRANSITION_DURATION,
    },
  },
  dawn: {
    y: '-200vh',
    transition: {
      duration: TRANSITION_DURATION,
    },
  },
  wraith: {
    y: '-100vh',
    transition: {
      duration: TRANSITION_DURATION,
    },
  },
  cullinan: {
    y: '-300vh',
    transition: {
      duration: TRANSITION_DURATION,
    },
  },
};

const slidesVariantsSM = {
  phantom: {
    y: '0rem',
    transition: {
      duration: TRANSITION_DURATION,
    },
  },
  dawn: {
    y: '-280rem',
    transition: {
      duration: TRANSITION_DURATION,
    },
  },
  wraith: {
    y: '-140rem',
    transition: {
      duration: TRANSITION_DURATION,
    },
  },
  cullinan: {
    y: '-420rem',
    transition: {
      duration: TRANSITION_DURATION,
    },
  },
};

const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const Offer = () => {
  const [currentApp, setCurrentApp] = useState(APP_NAMES[0]);

  const { ghostSticker, phantomImage, wraithImage, dawnImage, cullinanImage } = useStaticQuery(
    IMAGE_QUERY
  );

  const APP_IMAGES = {
    phantom: phantomImage,
    dawn: dawnImage,
    wraith: wraithImage,
    cullinan: cullinanImage,
  };

  const tabInteractionHandler = (name) => ({ type, key }) => {
    if (type === 'click' || (key && key === 'Enter')) {
      setCurrentApp(name);
    }
  };

  const deviceType = useDeviceType();

  const windowWidth = useWindowWidth();

  return (
    <section className={cx('container', 'wrapper')} id="products">
      {!(
        deviceType === DEVICE_TYPE.tablet.portrait || deviceType === DEVICE_TYPE.mobile.portrait
      ) && (
        <>
          <div className={cx('left', `left-${currentApp}`)}>
            <Heading
              className={cx('left-title')}
              tag="h2"
              size="sm"
              theme="primary"
              innerHTML="we offer"
            />
            <div className={cx('tabs')}>
              {APP_NAMES.map((name, i) => {
                return (
                  <button
                    className={cx('tab')}
                    type="button"
                    tabIndex={0}
                    key={name}
                    onClick={tabInteractionHandler(name)}
                    onKeyDown={tabInteractionHandler(name)}
                  >
                    <Heading
                      className={cx('button-title')}
                      tag="h4"
                      size="lg"
                      theme="primary-inverted"
                    >
                      {name}
                    </Heading>
                  </button>
                );
              })}
              <motion.div
                variants={windowWidth > BREAKPOINT_LG ? arrowVariants : arrowVariantsSmallScreen}
                animate={currentApp}
                initial={currentApp}
                className={cx('button-arrow')}
              >
                <span className={cx('button-arrow-text')}>
                  {`${capitalizeFirstLetter(currentApp)} descripion `.repeat(5)}
                </span>
              </motion.div>
            </div>
            <div className={cx('sticker')}>
              <GatsbyImage fluid={ghostSticker.childImageSharp.fluid} fadeIn={false} />
            </div>
          </div>
          <div className={cx('right')}>
            <motion.div
              className={cx('slides')}
              variants={
                deviceType === DEVICE_TYPE.mobile.landscape ? slidesVariantsSM : slidesVariants
              }
              animate={currentApp}
              initial={currentApp}
            >
              {APP_NAMES.map((appName) => (
                <Slide
                  key={`slide-${appName}`}
                  title={appName}
                  description={APPS_CONTENT[appName].description}
                  appImage={APP_IMAGES[appName]}
                  link={APPS_CONTENT[appName].link}
                />
              ))}
            </motion.div>
          </div>
        </>
      )}
      {(deviceType === DEVICE_TYPE.tablet.portrait ||
        deviceType === DEVICE_TYPE.mobile.portrait) && (
        <OfferVertical appImages={APP_IMAGES} sticker={ghostSticker} />
      )}
    </section>
  );
};

export default Offer;

const IMAGE_QUERY = graphql`
  query {
    ghostSticker: file(relativePath: { eq: "pages/home/offer/sticker.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    phantomImage: file(relativePath: { eq: "pages/home/offer/phantom.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    wraithImage: file(relativePath: { eq: "pages/home/offer/wraith.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    dawnImage: file(relativePath: { eq: "pages/home/offer/dawn.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    cullinanImage: file(relativePath: { eq: "pages/home/offer/cullinan.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
  }
`;
